/**************************************************************************************************/
/*   File: /src/app/stores/selectors/root/selectors.ts                                            */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Mon Apr 29 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { createSelector, MemoizedSelector } from '@ngrx/store';

import * as fromShared from '../../../shared';
import * as fromAuthorization from '../authorization';
import * as fromDossiers from '../dossiers';
import * as fromDownload from '../download';
import * as fromCreation from '../creation';
import * as fromDetails from '../details';
import * as fromEditing from '../editing';
import * as fromUi from '../ui';

/**
 * Get the url
 */
export const getIsLoading: MemoizedSelector<object, boolean> = createSelector(
    fromUi.getIsLoading,
    fromCreation.getIsLoading,
    fromDetails.getIsLoading,
    fromEditing.getIsLoading,
    fromDownload.getIsLoading,
    fromAuthorization.getIsLoading,
    fromDossiers.getIsLoading,

    (
        uiIsLoading: boolean,
        creationIsLoading: boolean,
        detailsIsLoading: boolean,
        editingIsLoading: boolean,
        downloadIsLoading: boolean,
        authorizationIsLoading: boolean,
        dossiersIsLoading: boolean
    ) => {
        return uiIsLoading ||
            creationIsLoading ||
            detailsIsLoading ||
            editingIsLoading ||
            downloadIsLoading ||
            authorizationIsLoading ||
            dossiersIsLoading;
    }
);
/**
 * Get application messages
 */
export const getMessages: MemoizedSelector<object, fromShared.models.Message[]> = createSelector(
    fromUi.getMessages,
    fromCreation.getMessages,
    fromDetails.getMessages,
    fromEditing.getMessages,
    fromDownload.getMessages,
    fromAuthorization.getMessages,
    fromDossiers.getMessages,

    (
        uiMessages: fromShared.models.Message[],
        creationMessages: fromShared.models.Message[],
        detailsMessages: fromShared.models.Message[],
        editingMessages: fromShared.models.Message[],
        downloadMessages: fromShared.models.Message[],
        authorizationMessages: fromShared.models.Message[],
        dossiersMessages: fromShared.models.Message[]
    ) => {
        return [
            ...uiMessages,
            ...creationMessages,
            ...detailsMessages,
            ...editingMessages,
            ...downloadMessages,
            ...authorizationMessages,
            ...dossiersMessages
        ];
    }
);

/**
 * Get application messages with popup destination
 */
export const getMessagesForPopup: MemoizedSelector<object, fromShared.models.Message[]> = createSelector(
    fromUi.getMessages,
    fromCreation.getMessages,
    fromDetails.getMessages,
    fromEditing.getMessages,
    fromDownload.getMessages,
    fromAuthorization.getMessages,
    fromDossiers.getMessages,
    (
        uiMessages: fromShared.models.Message[],
        creationMessages: fromShared.models.Message[],
        detailsMessages: fromShared.models.Message[],
        editingMessages: fromShared.models.Message[],
        downloadMessages: fromShared.models.Message[],
        authorizationMessages: fromShared.models.Message[],
        dossiersMessages: fromShared.models.Message[]
    ) => {
        const filter = (message: fromShared.models.Message) => message.position === fromShared.enums.MessagePositionEnum.POPUP;

        return [
            ...uiMessages.filter(filter),
            ...creationMessages.filter(filter),
            ...detailsMessages.filter(filter),
            ...editingMessages.filter(filter),
            ...downloadMessages.filter(filter),
            ...authorizationMessages.filter(filter),
            ...dossiersMessages.filter(filter)
        ];
    }
);

/**
 * Get application messages with top destination
 */
export const getMessagesForTop: MemoizedSelector<object, fromShared.models.Message[]> = createSelector(
    fromUi.getMessages,
    fromCreation.getMessages,
    fromDetails.getMessages,
    fromEditing.getMessages,
    fromDownload.getMessages,
    fromAuthorization.getMessages,
    fromDossiers.getMessages,
    (
        uiMessages: fromShared.models.Message[],
        creationMessages: fromShared.models.Message[],
        detailsMessages: fromShared.models.Message[],
        editingMessages: fromShared.models.Message[],
        downloadMessages: fromShared.models.Message[],
        authorizationMessages: fromShared.models.Message[],
        dossiersMessages: fromShared.models.Message[]
    ) => {
        const filter = (message: fromShared.models.Message) => message.position === fromShared.enums.MessagePositionEnum.TOP;
        return [
            ...uiMessages.filter(filter),
            ...creationMessages.filter(filter),
            ...detailsMessages.filter(filter),
            ...editingMessages.filter(filter),
            ...downloadMessages.filter(filter),
            ...authorizationMessages.filter(filter),
            ...dossiersMessages.filter(filter)
        ];
    }
);